import {
  type PropsWithChildren,
  createContext,
  useContext,
  useState
} from 'react';
import { z } from 'zod';

export const CANCEL_MODAL = 'cancelModal';
export const CONFIRMATION_MODAL = 'confirmationModal';
export const FORM_DRAWER = 'formDrawer';
export const CHECKLESS_PAYMENTS_MODAL = 'CHECKLESS_PAYMENTS_MODAL';
export const SPECIAL_OCCASION_MODAL = 'SPECIAL_OCCASION_MODAL';
export const SPECIAL_REQUEST_MODAL = 'SPECIAL_REQUEST_MODAL';
export const GET_HELP_MODAL = 'GET_HELP_MODAL';
export const CANCEL_DRAWER = 'CANCEL_DRAWER';
export const LEAVE_WAITLIST_DRAWER = 'LEAVE_WAITLIST_DRAWER';

const ModalTypeEnum = z.enum([
  CANCEL_MODAL,
  CONFIRMATION_MODAL,
  FORM_DRAWER,
  CHECKLESS_PAYMENTS_MODAL,
  SPECIAL_OCCASION_MODAL,
  SPECIAL_REQUEST_MODAL,
  GET_HELP_MODAL,
  CANCEL_DRAWER,
  LEAVE_WAITLIST_DRAWER
]);

export type ModalType = z.infer<typeof ModalTypeEnum>;

interface ModalMethods {
  isModalOpen: boolean;
  handleModalOpen: () => void;
  handleModalClose: () => void;
}

interface ModalContextInterface {
  [CANCEL_MODAL]: ModalMethods;
  [CONFIRMATION_MODAL]: ModalMethods;
  [FORM_DRAWER]: ModalMethods;
  [CHECKLESS_PAYMENTS_MODAL]: ModalMethods;
  [SPECIAL_OCCASION_MODAL]: ModalMethods;
  [SPECIAL_REQUEST_MODAL]: ModalMethods;
  [GET_HELP_MODAL]: ModalMethods;
  [CANCEL_DRAWER]: ModalMethods;
  [LEAVE_WAITLIST_DRAWER]: ModalMethods;
}

const ModalContext = createContext<ModalContextInterface | null>(null);

export const ModalProvider = ({ children }: PropsWithChildren<{}>) => {
  const useModalMethods = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const handleModalClose = () => setIsModalOpen(false);
    const handleModalOpen = () => setIsModalOpen(true);

    return { isModalOpen, handleModalOpen, handleModalClose };
  };

  return (
    <ModalContext.Provider
      value={{
        [CANCEL_MODAL]: useModalMethods(),
        [CONFIRMATION_MODAL]: useModalMethods(),
        [FORM_DRAWER]: useModalMethods(),
        [CHECKLESS_PAYMENTS_MODAL]: useModalMethods(),
        [SPECIAL_OCCASION_MODAL]: useModalMethods(),
        [SPECIAL_REQUEST_MODAL]: useModalMethods(),
        [GET_HELP_MODAL]: useModalMethods(),
        [CANCEL_DRAWER]: useModalMethods(),
        [LEAVE_WAITLIST_DRAWER]: useModalMethods()
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = (modalType: ModalType) => {
  const context = useContext(ModalContext);
  if (context == null) {
    throw new Error('useModal must be used within ModalProvider');
  }

  return context[modalType];
};
